import { useSearchQuery } from '@Components/Search/SearchPanel/useSearchQuery'
import { PaginationBody } from '@Entities/pagination'
import { Works } from '@Entities/work/works'
import { QueryPartyStore } from '@App/stores/search/party/query-party-store'

export const PARTY_WORKS_QUERY_KEY = 'party-works'

/**
 * The works of the party picked from a query-driven party search, as a TanStack Query. The store owns the picked
 * party, page and page size (the key is built from them), so picking a party, paging and changing the page size
 * are all store writes. Pass `undefined` for a store that still searches imperatively; nothing is fetched then.
 *
 * The works are copied into the store from the query's data, not from the fetch, so a cache hit reaches it too.
 */
export const usePartyWorksQuery = <resultType>(store: QueryPartyStore<resultType, object> | undefined) => {
    const party = store?.selectedParty
    const partyId = store?.selectedPartyId
    const pagination: PaginationBody = {
        page: store?.secondaryPage ?? 1,
        limit: store?.secondaryResultsPerPageLimit ?? 10,
    }

    const worksQuery = useSearchQuery<Works>({
        queryKey: [PARTY_WORKS_QUERY_KEY, store?.partySearchScope, partyId, pagination],
        queryFn: async () => {
            if (!store || !party) {
                return { works: [], count: 0 }
            }
            return store.fetchPartyWorks(party, pagination)
        },
        initialData: store?.restoredPartyWorks,
        applyData: (works) => store?.applyPartyWorks(works, pagination),
        enabled: !!party,
        onError: (error) => store?.reportSearchError(error),
    })

    return worksQuery
}

export default usePartyWorksQuery
